import React, { useContext, useEffect, useReducer } from 'react'
import {
  Cell,
  Row,
  Grid,
  TextField,
  Expander,
  ExpanderContent,
  Button,
  RadioGroup,
  Radio,
  H5,
  Paragraph,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { PersonContext } from '../PersonContext'
import { putGenderConclusion } from '../../../services/personService'
import { getLastPathElement } from '../../../util/StringUtil'
import PSAttribution from './PSAttribution'
import PSDivider from './PSDivider'
import useNotifications from '../../../hooks/useNotifications'
import { deepCopy } from '../../../util/Utils'

export default function SexSummary({ editable }) {
  const { currentPerson } = useContext(PersonContext)
  const { genderState, genderActions } = useGenderState()

  const { t } = useTranslation()

  useEffect(() => {
    const initialGender = getLastPathElement(genderState.constants.initialGenderConclusion.value?.type)
    const initialChangeMessage = genderState.constants.initialGenderConclusion.attribution.changeMessage
    const currentGender = genderState.variables.gender
    genderActions.setChanged(
      initialGender !== currentGender ||
        initialChangeMessage !== genderState.variables.genderConclusion.attribution.changeMessage
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genderState.variables.gender, genderState.variables.genderConclusion.attribution.changeMessage])

  if (!(currentPerson && genderState.variables.gender)) {
    return null
  }

  return (
    <>
      <div>
        <H5 supplementary>{t('sex', 'Sex')}</H5>
      </div>
      <Grid>
        {editable ? (
          <Cell columns={2}>
            <RadioGroup orientation="vertical">
              <Radio
                disabled={!editable}
                checked={genderState.variables.gender === 'Male'}
                label={t('gender.male', 'Male')}
                name="genderType"
                value="Male"
                id="summarySexMale"
                onChange={(event) => genderActions.setGender(event.target.value)}
              />
              <Radio
                disabled={!editable}
                checked={genderState.variables.gender === 'Female'}
                label={t('gender.female', 'Female')}
                name="genderType"
                value="Female"
                id="summarySexFemale"
                onChange={(event) => genderActions.setGender(event.target.value)}
              />
              <Radio
                disabled={!editable}
                checked={genderState.variables.gender === 'Unknown'}
                label={t('gender.unknown', 'Unknown')}
                name="genderType"
                value="Unknown"
                id="summarySexUnknown"
                onChange={(event) => genderActions.setGender(event.target.value)}
              />
            </RadioGroup>
          </Cell>
        ) : (
          <Cell alignY="middle" columns={2}>
            <Paragraph id="SummarySex" size="sm">
              {genderState.variables.gender}
            </Paragraph>
          </Cell>
        )}
        {editable && (
          <>
            <TextField
              role="textbox"
              id="genderChangeMessage"
              label={t('reason', 'Reason')}
              value={genderState.variables.genderConclusion.attribution.changeMessage}
              onChange={(event) => genderActions.setChangeMessage(event.target.value)}
            />
            <Expander setOpen={genderActions.setChanged} open={genderState.variables.changed}>
              <ExpanderContent data-testid="gender-summary-reason">
                <Row>
                  <Button emphasis="high" type="submit" id="summarySexSave" onClick={genderActions.saveGender}>
                    {t('action.save', 'Save')}
                  </Button>
                  <Button emphasis="medium" id="summarySexCancel" onClick={genderActions.reset}>
                    {t('action.cancel', 'Cancel')}
                  </Button>
                </Row>
              </ExpanderContent>
            </Expander>
          </>
        )}
        <PSAttribution attribution={currentPerson.genderConclusion.attribution} cellColumns={10} />
      </Grid>

      <PSDivider />
    </>
  )
}

function useGenderState() {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)

  const initialGenderConclusion = {
    conclusionType: deepCopy(currentPerson?.genderConclusion?.conclusionType),
    conclusionId: deepCopy(currentPerson?.genderConclusion?.conclusionId),
    value: {
      type: deepCopy(currentPerson?.genderConclusion?.value?.type),
    },
    attribution: {
      changeMessage: deepCopy(currentPerson?.genderConclusion?.attribution?.changeMessage),
    },
  }
  const initialState = {
    constants: {
      initialGenderConclusion: deepCopy(initialGenderConclusion),
      gedcomxGenderPrefix: 'http://gedcomx.org/',
    },
    variables: {
      genderConclusion: deepCopy(initialGenderConclusion),
      gender: getLastPathElement(initialGenderConclusion.value?.type),
      changed: false,
    },
  }
  const reducer = (_, currentState) => JSON.parse(JSON.stringify(currentState))
  const [genderState, updateGenderState] = useReducer(reducer, initialState)

  const genderActions = {
    setGender: (gender) => {
      genderState.variables.gender = gender
      genderState.variables.genderConclusion.value.type = genderState.constants.gedcomxGenderPrefix + gender
      updateGenderState(genderState)
    },
    setChanged: (changed) => {
      genderState.variables.changed = changed
      updateGenderState(genderState)
    },
    setChangeMessage: (changeMessage) => {
      genderState.variables.genderConclusion.attribution.changeMessage = changeMessage
      updateGenderState(genderState)
    },
    reset: () => {
      genderState.variables.genderConclusion = deepCopy(genderState.constants.initialGenderConclusion)
      genderState.variables.gender = getLastPathElement(genderState.constants.initialGenderConclusion.value?.type)
      genderState.changed = false
      updateGenderState(genderState)
    },
    saveGender: () => {
      putGenderConclusion(currentPerson.id, genderState.variables.genderConclusion)
        .then((response) => {
          if (response.data.error) {
            error(
              t('person.summary.errors.editingPersonGender', {
                defaultValue: `Error editing person gender: ${response.data.error || ''}`,
                message: response.data.error || '',
              }),
              response.data.error
            )
          } else {
            genderActions.reset()
            fetchCurrentPerson()
          }
        })
        .catch((err) => {
          error(
            t('person.summary.errors.editingPersonGender', {
              defaultValue: `Error editing person gender: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    },
  }

  return { genderState, genderActions }
}
